// Statements & exports — "do their own reporting". docs/CLIENT_PORTAL.md §7.
//
// Three views an accounts-payable department recognises, all dated by the
// COMPANY's calendar (the server sends days, not timestamps):
//   · what is owed now — every open invoice, overdue first
//   · activity between two days — invoiced, and paid, each totalled; with a
//     CSV of each for their own books
//   · a year-end summary — everything paid in a calendar year, per client
// A part-payment has no date of its own, so it is listed apart and never
// placed in a range; for the same reason there is no opening balance.

import React, { useCallback, useEffect, useState } from 'react';
import { EmptyState, FwButton, FwField, FwInput, FwSelect, Notice, Pill, RecordList, SectionLabel, Skeleton, Stat } from '../../../ui/fw';
import { get, money, day, downloadUrl } from '../portalApi';
import { invoiceState, openInvoice } from '../portalShared';

const isDay = (v) => /^\d{4}-\d{2}-\d{2}$/.test(v || '');

export default function Statements({ me, onUnauthorized }) {
  const billingClients = (me.clients || []).filter((c) => c.access === 'billing');
  const several = billingClients.length > 1;
  const [open, setOpen] = useState(null);
  const [clientId, setClientId] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [activity, setActivity] = useState(null);
  const [year, setYear] = useState('');
  const [yearEnd, setYearEnd] = useState(null);
  const [error, setError] = useState(null);
  const [rangeError, setRangeError] = useState(null);
  const [busy, setBusy] = useState(false);

  const fail = useCallback((e, setter) => { if (e.status === 401) onUnauthorized(); else setter(e.message); }, [onUnauthorized]);

  const query = (extra) => {
    const q = new URLSearchParams(extra);
    if (clientId) q.set('client_id', clientId);
    return q.toString();
  };

  const loadActivity = useCallback(async (f, t, cid) => {
    setRangeError(null);
    if (!isDay(f) || !isDay(t)) { setRangeError('Pick both dates.'); return; }
    if (f > t) { setRangeError('The first date must be on or before the second.'); return; }
    setBusy(true);
    try {
      const q = new URLSearchParams({ from: f, to: t });
      if (cid) q.set('client_id', cid);
      setActivity(await get(`/portal/statements/activity?${q.toString()}`));
    } catch (e) { fail(e, setRangeError); } finally { setBusy(false); }
  }, [fail]);

  const loadYear = useCallback(async (y, cid) => {
    try {
      const q = new URLSearchParams({ year: y });
      if (cid) q.set('client_id', cid);
      setYearEnd(await get(`/portal/statements/year?${q.toString()}`));
    } catch (e) { fail(e, setError); }
  }, [fail]);

  // Open items first: its as_of is the company's today, which sets the
  // default range (the year so far) and year.
  const loadOpen = useCallback(async (cid, first) => {
    setError(null);
    try {
      const q = new URLSearchParams();
      if (cid) q.set('client_id', cid);
      const o = await get(`/portal/statements/open${q.toString() ? `?${q.toString()}` : ''}`);
      setOpen(o);
      if (first) {
        const y = o.as_of.slice(0, 4);
        setFrom(`${y}-01-01`); setTo(o.as_of); setYear(y);
        loadActivity(`${y}-01-01`, o.as_of, cid);
        loadYear(y, cid);
      }
    } catch (e) { fail(e, setError); }
  }, [fail, loadActivity, loadYear]);

  useEffect(() => { loadOpen('', true); }, [loadOpen]);

  const onClient = (cid) => {
    setClientId(cid);
    loadOpen(cid, false);
    if (from && to) loadActivity(from, to, cid);
    if (year) loadYear(year, cid);
  };

  if (error && !open) return <Notice tone="stop" title="Statements could not be loaded" data-note="load-failed">{error}</Notice>;
  if (!open) return <Skeleton lines={6} />;

  const years = [];
  const thisYear = Number(open.as_of.slice(0, 4));
  for (let y = thisYear; y >= thisYear - 6; y -= 1) years.push(String(y));

  const owedColumns = [
    { key: 'invoice_number', label: 'Invoice', role: 'title' },
    ...(several ? [{ key: 'client_name', label: 'For', role: 'subtitle' }] : []),
    { key: 'state', label: 'Status', role: 'badge', render: (r) => { const s = invoiceState(r); return <Pill tone={s.tone}>{s.label}</Pill>; } },
    { key: 'balance', label: 'Balance', align: 'right', render: (r) => money(r.balance_cents) },
    { key: 'issued', label: 'Issued', render: (r) => day(r.issued_on) },
    { key: 'due', label: 'Due', render: (r) => day(r.due_on) },
  ];
  const lineColumns = (when) => [
    { key: 'invoice_number', label: 'Invoice', role: 'title' },
    ...(several ? [{ key: 'client_name', label: 'For', role: 'subtitle' }] : []),
    { key: 'amount', label: when === 'paid' ? 'Paid' : 'Total', align: 'right', render: (r) => money(when === 'paid' ? r.paid_cents : r.total_cents) },
    { key: 'on', label: when === 'paid' ? 'Paid on' : 'Issued', render: (r) => day(when === 'paid' ? r.paid_on : r.issued_on) },
  ];

  return (
    <div className="pt-screen" data-screen="portal-statements">
      <h1 className="pt-h1">Statements</h1>
      {several ? (
        <div className="pt-filters">
          <FwField label="For" htmlFor="pt-st-client">
            <FwSelect id="pt-st-client" value={clientId} onChange={(e) => onClient(e.target.value)}>
              <option value="">Everyone</option>
              {billingClients.map((c) => <option key={c.id} value={String(c.id)}>{c.name}</option>)}
            </FwSelect>
          </FwField>
        </div>
      ) : null}
      {error ? <Notice tone="stop" data-note="load-failed">{error}</Notice> : null}

      <section className="pt-section" data-section="open">
        <SectionLabel>What is owed now</SectionLabel>
        <div className="pt-stats">
          <Stat label="Owed" value={money(open.owed_cents)} data-figure="owed" />
          <Stat label="Overdue" value={money(open.overdue_cents)} tone={open.overdue_cents > 0 ? 'stop' : undefined} data-figure="overdue" />
        </div>
        <p className="pt-muted">{`As of ${day(open.as_of)}.`}</p>
        {open.rows.length
          ? <RecordList columns={owedColumns} rows={open.rows} onRowClick={openInvoice} caption="What is owed now" />
          : <EmptyState icon="check" title="Nothing owed">Every invoice is paid.</EmptyState>}
      </section>

      <section className="pt-section" data-section="activity">
        <SectionLabel>Activity</SectionLabel>
        <form className="pt-range" onSubmit={(e) => { e.preventDefault(); loadActivity(from, to, clientId); }} noValidate>
          <FwField label="From" htmlFor="pt-from"><FwInput id="pt-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></FwField>
          <FwField label="To" htmlFor="pt-to"><FwInput id="pt-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} /></FwField>
          <div className="pt-range__go"><FwButton type="submit" variant="ghost" loading={busy} data-action="show-activity">Show</FwButton></div>
        </form>
        {rangeError ? <Notice tone="warn" data-note="range">{rangeError}</Notice> : null}
        {activity ? (
          <>
            <div className="pt-stats">
              <Stat label="Invoiced" value={money(activity.invoiced_cents)} hint={`${activity.invoiced.length} invoice${activity.invoiced.length === 1 ? '' : 's'}`} data-figure="invoiced" />
              <Stat label="Paid" value={money(activity.paid_cents)} hint={`${activity.paid.length} payment${activity.paid.length === 1 ? '' : 's'}`} data-figure="paid" />
            </div>
            <p className="pt-muted">{`${day(activity.from)} to ${day(activity.to)}.`}</p>
            <div className="pt-downloads">
              <a className="fw-btn fw-btn--ghost fw-btn--md" href={downloadUrl(`/portal/export/invoices.csv?${query({ from: activity.from, to: activity.to })}`)} download data-action="download-invoices">Download invoices (CSV)</a>
              <a className="fw-btn fw-btn--ghost fw-btn--md" href={downloadUrl(`/portal/export/payments.csv?${query({ from: activity.from, to: activity.to })}`)} download data-action="download-payments">Download payments (CSV)</a>
            </div>
            {activity.undated_part_payments && activity.undated_part_payments.count ? (
              <Notice tone="info" data-note="part-payments">
                {`${money(activity.undated_part_payments.cents)} has been paid toward ${activity.undated_part_payments.invoices.join(', ')}, which ${activity.undated_part_payments.count === 1 ? 'is' : 'are'} not paid in full yet. A part-payment has no date of its own here, so it is not in any range; it shows on the invoice.`}
              </Notice>
            ) : null}
            <h3 className="pt-h3">Invoiced</h3>
            {activity.invoiced.length ? <RecordList columns={lineColumns('issued')} rows={activity.invoiced} caption="Invoiced" /> : <p className="pt-muted">No invoices in these dates.</p>}
            <h3 className="pt-h3">Paid</h3>
            {activity.paid.length ? <RecordList columns={lineColumns('paid')} rows={activity.paid} caption="Paid" /> : <p className="pt-muted">No payments in these dates.</p>}
          </>
        ) : null}
      </section>

      <section className="pt-section" data-section="year">
        <SectionLabel>Year-end summary</SectionLabel>
        <div className="pt-filters">
          <FwField label="Year" htmlFor="pt-year">
            <FwSelect id="pt-year" value={year} onChange={(e) => { setYear(e.target.value); loadYear(e.target.value, clientId); }}>
              {years.map((y) => <option key={y} value={y}>{y}</option>)}
            </FwSelect>
          </FwField>
        </div>
        {yearEnd ? (
          <>
            <Stat label={`Paid in ${yearEnd.year}`} value={money(yearEnd.paid_cents)} data-figure="year-paid" />
            {yearEnd.clients.length > 1 ? (
              <RecordList
                columns={[
                  { key: 'client_name', label: 'For', role: 'title' },
                  { key: 'paid', label: 'Paid', align: 'right', render: (r) => money(r.paid_cents) },
                  { key: 'invoices', label: 'Invoices', align: 'right', render: (r) => String(r.invoices) },
                ]}
                rows={yearEnd.clients}
                rowKey="client_id"
                caption={`Paid in ${yearEnd.year}, by client`}
              />
            ) : null}
            {yearEnd.paid.length ? null : <p className="pt-muted">{`Nothing was paid in ${yearEnd.year}.`}</p>}
            <div className="pt-downloads">
              <a className="fw-btn fw-btn--ghost fw-btn--md" href={downloadUrl(`/portal/export/payments.csv?${query({ from: `${yearEnd.year}-01-01`, to: `${yearEnd.year}-12-31` })}`)} download data-action="download-year">{`Download ${yearEnd.year} payments (CSV)`}</a>
            </div>
          </>
        ) : null}
      </section>
    </div>
  );
}
